/**
 * Reporte Gastos Taller — migración web de ares9025.
 *
 * Consulta de solo lectura de las órdenes de taller por rango de fechas y
 * sucursal (opcional), con el total de gastos del período. Exporta a Excel
 * con fila de total e imprime el listado.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi, usePerfilSucursal } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin órdenes de taller en el rango
      </Typography>
    </Stack>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales });
}

export default function ReporteTaller() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { esadmin, sucursalPropia } = usePerfilSucursal();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [sucursal, setSucursal] = useState("");
  const [rows, setRows] = useState([]);
  const [totalgastos, setTotalgastos] = useState(0);
  const [loading, setLoading] = useState(false);

  // Un usuario que no es admin queda fijado a su sucursal.
  useEffect(() => {
    if (!esadmin && sucursalPropia) setSucursal(sucursalPropia);
  }, [esadmin, sucursalPropia]);

  const cargar = async () => {
    setLoading(true);
    try {
      const body = { fechadesde: fecha1, fechahasta: fecha2 };
      if (sucursal.trim()) body.sucursal = sucursal.trim();
      const { ok, data } = await post("/api/taller_ordenes", body);
      if (ok) {
        setRows((data.ordenes || []).map((r) => ({ ...r, id: r.id })));
        setTotalgastos(data.totalgastos || 0);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estadoLabel = (r) =>
    r.status === "C" ? `Cerrada${r.devueltoafabrica === "S" ? " (fábrica)" : ""}` : "Abierta";

  // Export a Excel con fila de total.
  const exportarExcel = () => {
    if (!rows.length) {
      showAlert({ message: "No hay datos para exportar", color: "warning" });
      return;
    }
    const encabezados = [
      "Orden",
      "Entrada",
      "Chasis",
      "Vehículo",
      "Daño físico",
      "Responsable",
      "Estado",
      "Gastos",
      "Pagado",
      "Salida",
      "Sucursal",
    ];
    const filas = rows.map((r) => [
      r.id,
      r.fecha,
      r.chasis,
      [r.marca, r.modelo].filter(Boolean).join(" "),
      r.danofisico,
      r.responsable,
      estadoLabel(r),
      r.gasto,
      r.pagadocliente,
      r.fechasalida,
      r.sucursal,
    ]);
    const hoja = XLSX.utils.aoa_to_sheet([
      encabezados,
      ...filas,
      [],
      ["", "", "", "", "", "", "TOTAL GASTOS:", totalgastos],
    ]);
    hoja["!cols"] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 22 },
      { wch: 22 },
      { wch: 35 },
      { wch: 18 },
      { wch: 16 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
    ];
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Gastos Taller");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
    XLSX.writeFile(libro, `ReporteGastosTaller_${stamp}.xlsx`);
  };

  const imprimir = () => {
    if (!rows.length) {
      showAlert({ message: "No hay datos para imprimir", color: "warning" });
      return;
    }
    imprimirListado(
      "Reporte Gastos Taller",
      `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}${sucursal.trim() ? ` · Sucursal ${sucursal.trim()}` : ""}`,
      ["Orden", "Entrada", "Chasis", "Vehículo", "Responsable", "Estado", "Gastos", "Pagado"],
      rows.map((r) => [
        r.id,
        r.fecha,
        r.chasis,
        [r.marca, r.modelo].filter(Boolean).join(" "),
        r.responsable || "",
        estadoLabel(r),
        fmtMoney(r.gasto),
        fmtMoney(r.pagadocliente),
      ]),
      ["", "", "", "", "", "TOTAL GASTOS:", fmtMoney(totalgastos), ""]
    );
  };

  const columns = [
    { field: "id", headerName: "Orden", width: 80 },
    { field: "fecha", headerName: "Entrada", width: 105 },
    { field: "chasis", headerName: "Chasis", width: 170 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 150,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
    { field: "danofisico", headerName: "Daño físico", flex: 1, minWidth: 150 },
    { field: "responsable", headerName: "Responsable", width: 130 },
    {
      field: "status",
      headerName: "Estado",
      width: 140,
      renderCell: (params) =>
        params.value === "C" ? (
          <Chip
            size="small"
            label={`Cerrada${params.row.devueltoafabrica === "S" ? " (fábrica)" : ""}`}
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Abierta"
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "gasto",
      headerName: "Gastos",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "pagadocliente",
      headerName: "Pagado",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    { field: "fechasalida", headerName: "Salida", width: 105 },
    { field: "sucursal", headerName: "Sucursal", width: 95 },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Reporte Gastos Taller"
        subtitle="Consulta los gastos de taller por fechas y sucursal, exporta a Excel e imprime"
        cta={
          <Stack direction="row" spacing={1.2}>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportarExcel}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Exportar Excel
            </Button>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Sucursal"
                placeholder="Todas"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                disabled={!esadmin}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5 }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} órdenes
              </Typography>
              <ResumenChip label="TOTAL GASTOS" value={`RD$ ${fmtMoney(totalgastos)}`} />
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
